import { EventEmitter, Injectable } from '@angular/core';
import {  InboxMessageGroup, Message, MessageRecipient, NewMessage, Poll, PollResponse, PollResponseOption, PollWithOptions, SendMessageReceipients, SendMessageResponse } from '../models/message';
import { HttpClient } from '@angular/common/http';
import { environment } from 'src/environments/environment';
import { catchError, tap } from 'rxjs';
import { NotificationService } from '../shared/notification.service';


@Injectable({
  providedIn: 'root'
})
export class CommService {

  private readonly DEFAULT_MESSAGE_TYPE = 2;
  private readonly DEFAULT_VALUE = 0;

  private apiUrl: string = "v1/messages/";

  constructor(private http: HttpClient, private notification: NotificationService) {}

  public sendMessage(message: NewMessage) {
    if (!this.isMessageValid(message)) {
      throw new Error('Message is not valid for sending');
    }
    const messageToSend = this.prepareMessageForSending(message);
    if (messageToSend.poll) {
      console.log("Associating poll with message group ID:", messageToSend.poll, messageToSend.messageGroupId);
    }
    return this.http.post<SendMessageResponse>(
      `${environment.ApiUrl()}${this.apiUrl}newMessage`, messageToSend
    ).pipe(
      catchError(() => {
        throw new Error('Error occurred while sending message');
      })
    );
  }


  public getMessages() {
    return this.http.get<InboxMessageGroup[]>(environment.ApiUrl() + this.apiUrl + 'mymessages')
    .pipe(catchError(
      (error) => {
        this.notification.showError('Error occurred while fetching messages:', error);
        throw error;
      }
    ));
  }

  public getAvailableRecipients() {
    return this.http.get<SendMessageReceipients>(environment.ApiUrl() + this.apiUrl + 'message/permissions')
    .pipe(catchError(
      (error) => {
        this.notification.showError('Error occurred while fetching permissions:', error);
        throw error;
      }
    ));
  }

  public getMessageRecipients(messageGroupUuid: string){
    return this.http.get<MessageRecipient[]>(environment.ApiUrl() + this.apiUrl + 'message/recipients/' + messageGroupUuid)
    .pipe(catchError(
      (error) => {
        this.notification.showError('Error occurred while fetching message recipients:', error);
        throw error;
      }
    ))
  }

  public deleteMessage(messageUuid: string) {
    return this.http.delete(environment.ApiUrl() + this.apiUrl + 'message/delete/' + messageUuid)
    .pipe(catchError(
      (error) => {
        this.notification.showError('Error occurred while deleting message:', error);
        throw error;
      }
    ));
  }

  private prepareMessageForSending(message: NewMessage): NewMessage {
    const uuid = crypto.randomUUID();
    return {
      ...message,
      dateCreated: new Date(),
      dateRead: null,
      messageOpened: false,
      messageGroupId: uuid,
      messageId: 0,
      value: this.DEFAULT_VALUE,
      messageType: this.DEFAULT_MESSAGE_TYPE,
      isPopup: message.isPopup ?? false,
      userId: 0,
      createdByUserId:0,
      action1Url: uuid,
      poll: message.poll ? {
        ...message.poll,
        inboxMessageGroupId: uuid
      } : undefined
    };
  }
  
  private isMessageValid(message: NewMessage): boolean {
  return !!(
    message.message?.trim() &&
    message.messageLong?.trim() &&
    message.sendConfig?.franchiseIds?.length &&
    message.sendConfig?.clientIds?.length &&
    message.sendConfig?.roleIds?.length
  );
}

  public newPoll(poll: Poll){
    return this.http.post<PollWithOptions>(environment.ApiUrl() + this.apiUrl + 'poll', poll)
    .pipe(
      catchError(
        (error) => {
          this.notification.showError('Error occurred while creating poll:', error);
          throw error;
        }
      )
    )
  }

  public addPollOption(pollOption: PollResponseOption){
    return this.http.post<PollWithOptions>(environment.ApiUrl() + this.apiUrl + 'poll/option', pollOption)
    .pipe(
      catchError(
        (error) => {
          this.notification.showError('Error occurred while adding poll option:', error);
          throw error;
        }
      )
    );
  }

  public newPollResponse(pollResponse: PollResponse) {
    return this.http.post<PollResponse>(environment.ApiUrl() + this.apiUrl + 'poll/response', pollResponse)
    .pipe(
      catchError(
        (error) => {
          this.notification.showError('Error occurred while creating poll response:', error);
          throw error;
        }
      )
    );
  }

  public deletePollOption(pollOptionId: number) {
    return this.http.delete<PollWithOptions>(environment.ApiUrl() + this.apiUrl + 'poll/option/' + pollOptionId)
    .pipe(
      catchError(
        (error) => {
          this.notification.showError('Error occurred while deleting poll option:', error);
          throw error;
        }
      )
    );
  }

  public deletePoll(pollId: number) {
    return this.http.delete(environment.ApiUrl() + this.apiUrl + 'poll/' + pollId)
    .pipe(
      catchError(
        (error) => {
          this.notification.showError('Error occurred while deleting poll:', error);
          throw error;
        }
      )
    );
  }
}
